import { useState, useEffect } from 'react';
import {
  useNavigate,
  useLocation,
  NavLink,
  Outlet,
  useMatch,
} from 'react-router-dom';
import queryString from 'query-string';
import {
  IconExpand,
  IconTrash,
  ButtonSecondary,
  ButtonSize,
  IconShow,
  IconHide,
  IconArrowLeft,
  InputText,
  IconSearch,
  ResponseError,
  PermissionEnum,
  IconDown,
  IconUp,
  ButtonPrimary,
  Checkbox,
  ClickOutsideDetector,
  ButtonPseudo,
  useQueryState,
  useGetList,
  usePrevious,
  Filters,
  SchemaFilterType,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  onSearch,
  hideInactive as setHideInactive,
  onSelectBlacklistsIds,
  setSkipValue,
} from './store/slices/list';
import { Blacklist } from '../types';
import {
  useToggleBlacklistStatusMutation,
  useLazyGetBlacklistsItemsQuery,
  useLazyGetBlacklistsDataQuery,
} from './store/services';

import ROUTES from '../../routes';

import cn from './blacklist.module.css';
import { blacklistUsersSchema } from './variables/user';

let timeout: NodeJS.Timeout;

const BlacklistWrapper = () => {
  const [searchValue, setSearchValue] = useState('');
  const [blackListsVisible, setBlacklistsVisible] = useState(false);
  const [selectedBlacklists, setSelectedBlackLists] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [active, setActive] = useState(true);
  const [entityId, setEntityId] = useState('');
  const [takeCount, setTakeCount] = useState(0);

  const match = useMatch(ROUTES.BLACKLIST_LIST_RESULTS);
  const wrapperMatch = useMatch(ROUTES.BLACKLIST_LIST);
  const usersMatch = useMatch(ROUTES.BLACKLIST_LIST_USERS);

  const {
    blacklist: {
      list: {
        values,
        hideInactive,
        selectedLists,
        searchText,
        selectedBlacklistsIds,
        blacklistsLists,
        searchResult,
        skipValue,
      },
    },
    amlDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [changeStatus] = useToggleBlacklistStatusMutation();
  const [getBlacklistsItems] = useLazyGetBlacklistsItemsQuery();

  const { sort, onSortChange, filters, onFilterChange } =
    useQueryState(blacklistUsersSchema);

  const [getBlacklistData, { isLoading, isFetching }] =
    useLazyGetBlacklistsDataQuery();

  const { onSort, onFilter, setPage, getListAction, errorList } = useGetList({
    getList: getBlacklistData,
    onFilterChange,
    onSortChange,
    schema: blacklistUsersSchema,
  });

  const prevFilters = usePrevious(filters);
  const prevSearchText = usePrevious(searchText);

  const onUserSearch = (value: string, lists?: string[]) => {
    clearTimeout(timeout);
    setSearchValue(value);

    timeout = setTimeout(() => {
      dispatch(onSearch(value));

      if (!lists && selectedIds?.length) {
        dispatch(onSelectBlacklistsIds(selectedIds));
      } else if (lists) {
        dispatch(onSelectBlacklistsIds(lists));
      }

      if (value) {
        const nextFilters: Filters = { ...filters };
        nextFilters.name = {
          value: [value || ''],
          type: SchemaFilterType.STRING,
        };

        if (nextFilters.cursor) {
          delete nextFilters.cursor;
        }

        if (lists) {
          nextFilters.blacklistId = {
            value: lists || [],
            type: SchemaFilterType.STRING,
          };
        }

        if (searchResult) {
          getListAction({
            filters: nextFilters,
            sort,
          });
        }

        if (value !== prevSearchText) {
          navigate(
            `${ROUTES.BLACKLIST_LIST_USERS}?name=${value}${
              selectedIds?.length
                ? `&blacklistId=${selectedIds?.join(`&blacklistId=`)}`
                : ''
            }`,
          );
        }

        clearTimeout(timeout);
      } else if (!value && !match) {
        navigate(ROUTES.BLACKLIST_LIST_RESULTS);
        dispatch(onSearch(''));
        dispatch(onSelectBlacklistsIds([]));
        setSelectedBlackLists([]);
        setSelectedIds([]);
      }
    }, 700);
  };

  const setInactive = () => {
    dispatch(setHideInactive(!hideInactive));

    setActive(!hideInactive);
  };

  const changeStatusAction = () => {
    selectedLists.map(async (item) => {
      const currentItem = values?.find((listItem) => listItem.id === item);

      if (currentItem) {
        (await changeStatus({
          id: item,
          active: !currentItem.active,
        })) as { data?: Blacklist; error?: ResponseError };
      }
    });
  };

  useEffect(() => {
    if (
      takeCount !== 0 &&
      sort &&
      (searchText || entityId) &&
      skipValue?.toString() !== 'undefined' &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !searchResult
    ) {
      const nextFilters: Filters = {
        ...filters,
        take: {
          value: [takeCount?.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      if (searchText) {
        nextFilters.name = {
          value: [searchText || ''],
          type: SchemaFilterType.STRING,
        };
      }

      if (skipValue && skipValue > 0) {
        nextFilters.cursor = {
          value: [skipValue.toString()],
          type: SchemaFilterType.STRING,
        };
      }

      if (entityId) {
        nextFilters.entityGUID = {
          value: [entityId || ''],
          type: SchemaFilterType.STRING,
        };
      }

      if (selectedBlacklistsIds) {
        nextFilters.blacklistId = {
          value: selectedBlacklistsIds || [],
          type: SchemaFilterType.STRING,
        };
      }

      if (filters.name && !searchText) {
        dispatch(onSearch(filters.name.value[0]));
        setSearchValue(filters.name.value[0]);
      }

      if (filters.blacklistId && !selectedBlacklistsIds) {
        if (Array.isArray(filters.blacklistId.value)) {
          dispatch(onSelectBlacklistsIds(filters.blacklistId.value));
          setSelectedIds(filters.blacklistId.value);
          setSelectedBlackLists(filters.blacklistId.value);
        } else {
          dispatch(onSelectBlacklistsIds([filters.blacklistId.value]));
          setSelectedIds([filters.blacklistId.value]);
          setSelectedBlackLists([filters.blacklistId.value]);
        }
      }

      onFilterChange(nextFilters);

      getListAction({
        filters: nextFilters,
        sort,
      });
    }
  }, [filters, sort, searchResult, takeCount, skipValue]);

  useEffect(() => {
    if (wrapperMatch) {
      navigate(ROUTES.BLACKLIST_LIST_RESULTS);
    }
  }, [wrapperMatch]);

  useEffect(() => {
    getBlacklistsItems();
  }, []);

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed) {
        if (parsed.entityGUID) {
          setEntityId(parsed.entityGUID as string);
        }

        if (
          parsed.name &&
          !searchText &&
          !values &&
          usersMatch &&
          prevSearchText === null
        ) {
          dispatch(onSearch(parsed.name as string));
          setSearchValue(parsed.name as string);
        }

        if (usersMatch && parsed.cursor) {
          dispatch(setSkipValue(Number(parsed.cursor)));
        } else {
          dispatch(setSkipValue(0));
        }

        if (parsed.blacklistId && !selectedBlacklistsIds) {
          if (Array.isArray(parsed.blacklistId)) {
            dispatch(onSelectBlacklistsIds(parsed.blacklistId as string[]));
            setSelectedIds(parsed.blacklistId as string[]);
            setSelectedBlackLists(parsed.blacklistId as string[]);
          } else {
            dispatch(onSelectBlacklistsIds([parsed.blacklistId]));
            setSelectedIds([parsed.blacklistId]);
            setSelectedBlackLists([parsed.blacklistId]);
          }
        }
      }
    } else {
      dispatch(setSkipValue(0));
    }
  }, [
    search,
    searchText,
    selectedBlacklistsIds,
    usersMatch,
    values,
    prevSearchText,
  ]);

  let selectListTitle = 'All list';

  if (selectedIds?.length === 1) {
    selectListTitle = blacklistsLists?.find(
      (item) => item.value === selectedIds[0],
    )?.name as string;
  } else if (selectedIds?.length > 1) {
    selectListTitle = 'Multiple lists';
  }

  return (
    <div className={cn.blacklistBottom}>
      <div className={cn.blacklistTopWrapper}>
        <div className={cn.blacklistTitleWrapper}>
          {(usersMatch || searchText) && (
            <NavLink
              to={ROUTES.BLACKLIST_LIST}
              className={cn.blacklistPageBackLink}
              onClick={() => {
                setSearchValue('');
                dispatch(onSearch(''));
                setSelectedIds([]);
                setSelectedBlackLists([]);
                dispatch(onSelectBlacklistsIds([]));
              }}
            >
              <IconArrowLeft width={13} height={11} />
              Back
            </NavLink>
          )}
          <div className={cn.blacklistTitle}>Block list</div>
        </div>
        <div className={cn.blacklistTop}>
          {activePermissions?.includes(
            PermissionEnum.BLACKLISTS_ADD_ACTION,
          ) && (
            <NavLink
              to={`${
                match
                  ? ROUTES.BLACKLIST_LIST_CREATE
                  : ROUTES.BLACKLIST_USERS_CREATE
              }${search || ''}`}
              className={cn.blacklistCreateLink}
            >
              <div className={cn.blacklistCreateLinkContent}>
                <IconExpand className={cn.blacklistCreateLinkIcon} />
                <span>Create list</span>
              </div>
            </NavLink>
          )}
          {match && (
            <>
              {activePermissions?.includes(
                PermissionEnum.BLACKLISTS_ADD_ACTION,
              ) && <div className={cn.blacklistTopSeparator} />}
              {!selectedLists.length && (
                <ButtonSecondary
                  className={`button-white ${cn.hideInactiveList}`}
                  size={ButtonSize.SMALL}
                  text={
                    hideInactive ? 'Show inactive lists' : 'Hide inactive lists'
                  }
                  icon={hideInactive ? <IconShow /> : <IconHide />}
                  onClick={() => {
                    setInactive();
                  }}
                />
              )}
              {selectedLists.length > 0 && (
                <ButtonSecondary
                  className={`button-white ${cn.hideInactiveList}`}
                  size={ButtonSize.SMALL}
                  icon={<IconTrash />}
                  text={`Deactivate/Activate ${selectedLists.length} rule${
                    selectedLists.length > 1 ? 's' : ''
                  }`}
                  onClick={() => changeStatusAction()}
                />
              )}
            </>
          )}
        </div>
        <div className={cn.searchInputWrapper}>
          <ClickOutsideDetector setOptionsListVisible={setBlacklistsVisible}>
            <div className={cn.blacklistsSelectorWrapper}>
              <button
                type="button"
                className={cn.showListsButton}
                onClick={() => setBlacklistsVisible(!blackListsVisible)}
              >
                <div className={cn.showListsButtonContent}>
                  {selectListTitle}
                  <div className={cn.iconWrapper}>
                    {!blackListsVisible ? <IconDown /> : <IconUp />}
                  </div>
                </div>
              </button>
              {blackListsVisible && (
                <div className={cn.blacklistsList}>
                  <div className={cn.blacklistsContent}>
                    {blacklistsLists?.map((item) => (
                      <Checkbox
                        name={`blacklist-${item.value}`}
                        type="checkbox"
                        title={item.name}
                        key={item.value}
                        labelClassName={cn.blacklistCheckbox}
                        checked={Boolean(
                          selectedBlacklists?.find((id) => item.value === id),
                        )}
                        onChange={() => {
                          let nextSelectedBlacklists = ([] as string[]).concat(
                            selectedBlacklists,
                          );

                          if (
                            selectedBlacklists.find((id) => item.value === id)
                          ) {
                            nextSelectedBlacklists =
                              nextSelectedBlacklists?.filter(
                                (id) => item.value !== id,
                              );
                          } else {
                            nextSelectedBlacklists.push(item.value as string);
                          }

                          setSelectedBlackLists(nextSelectedBlacklists);
                        }}
                      />
                    ))}
                  </div>
                  <div className={cn.blacklistsFooter}>
                    <ButtonPrimary
                      size={ButtonSize.SMALL}
                      text="Apply"
                      onClick={() => {
                        setSelectedIds(selectedBlacklists);
                        setBlacklistsVisible(false);

                        if (usersMatch) {
                          dispatch(onSelectBlacklistsIds(selectedBlacklists));

                          onUserSearch(searchValue, selectedBlacklists);
                        }
                      }}
                    />
                    <ButtonPseudo
                      text="Clear"
                      size={ButtonSize.SMALL}
                      className={cn.clearButton}
                      onClick={() => {
                        setBlacklistsVisible(false);
                        setSelectedBlackLists([]);
                        setSelectedIds([]);
                        dispatch(onSelectBlacklistsIds([]));

                        if (usersMatch) {
                          onUserSearch(searchValue, []);
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </ClickOutsideDetector>

          <div className={cn.searchInputContent}>
            <InputText
              name="searchText"
              placeholder="Search all lists by name..."
              prefix={<IconSearch />}
              value={searchValue}
              onChange={(e) => onUserSearch(e.target.value)}
              labelClassName={cn.searchInputLabel}
              className={cn.searchInput}
            />
          </div>
        </div>
      </div>
      <Outlet
        context={{
          active,
          setSearchValue,
          setSelectedIds,
          setSelectedBlackLists,
          isFetching,
          isLoading,
          errorList,
          onSort,
          filters,
          sort,
          onFilter,
          setPage,
          takeCount,
          setTakeCount,
        }}
      />
    </div>
  );
};

export default BlacklistWrapper;
